import { useState } from 'react'
import { message as antMessage } from 'antd'
import type { RcFile } from 'antd/es/upload'
import { useTranslation } from 'react-i18next'
import {
  MAX_OUTBOUND_ATTACHMENTS,
  MAX_OUTBOUND_ATTACHMENT_BYTES,
  MAX_OUTBOUND_TOTAL_BYTES,
} from '../../lib/shared/chatwootAttachments'

// Shared attachment state for every surface that sends outbound files — the
// customer bubble's composer and new-conversation form, and the operator's
// new-conversation modal — so all of them offer the same picker + validation.
// Files are validated on select and staged on submit. Lives beside
// attachmentUploads.ts, the other outbound-attachment module both sides import.
export function useOutboundAttachments() {
  const { t } = useTranslation()
  const [attachments, setAttachments] = useState<RcFile[]>([])

  // AntD calls beforeUpload once per selected file; process the whole batch on
  // the first and skip the rest, so size/count toasts fire at most once per
  // selection. Returning false prevents AntD's auto-upload — files stage on
  // submit. Capacity + toasts stay out of the state updater (StrictMode
  // double-runs it), matching the operator composer.
  function onSelect(file: RcFile, batch: RcFile[]): boolean {
    if (file !== batch[0]) {
      return false
    }
    const oversized = batch.filter(
      (f) => f.size > MAX_OUTBOUND_ATTACHMENT_BYTES
    )
    if (oversized.length > 0) {
      antMessage.error(
        t('frontend:chatwoot.attachment.tooLarge', {
          name: oversized.map((f) => f.name).join(', '),
        })
      )
    }
    const withinSize = batch.filter(
      (f) => f.size <= MAX_OUTBOUND_ATTACHMENT_BYTES
    )
    const capacity = Math.max(0, MAX_OUTBOUND_ATTACHMENTS - attachments.length)
    const withinCapacity = withinSize.slice(0, capacity)
    if (withinSize.length > capacity) {
      antMessage.error(
        t('frontend:chatwoot.attachment.tooMany', {
          max: MAX_OUTBOUND_ATTACHMENTS,
        })
      )
    }
    // Also enforce the backend's combined-size cap here, so several individually
    // small files can't be staged + uploaded only to be rejected upstream.
    let runningTotal = attachments.reduce((sum, f) => sum + f.size, 0)
    const accepted: RcFile[] = []
    let droppedForTotal = false
    for (const file of withinCapacity) {
      if (runningTotal + file.size > MAX_OUTBOUND_TOTAL_BYTES) {
        droppedForTotal = true
        continue
      }
      runningTotal += file.size
      accepted.push(file)
    }
    if (droppedForTotal) {
      antMessage.error(t('frontend:chatwoot.attachment.totalTooLarge'))
    }
    if (accepted.length > 0) {
      setAttachments((current) => [...current, ...accepted])
    }
    return false
  }

  function remove(target: RcFile) {
    setAttachments((current) => current.filter((file) => file !== target))
  }

  function reset() {
    setAttachments([])
  }

  // Set the whole selection at once, bypassing the pickers' validation: the only
  // caller is the restore of a previously validated, persisted pick.
  function replace(files: RcFile[]) {
    setAttachments(files)
  }

  return { attachments, onSelect, remove, reset, replace }
}
